import SelectField from "../../../../common/SelectField";
import MultiSelectDrawer from "../../../../common/MultiSelectDrawer";
import { isSingleSelectSeriesKey } from "../../../../../../utils/planManager/networkConfigurationUtils";

const PREFERRED_SERIES_KEY = "900 series";
const CONNECTED_PAIR_KEYS = Object.freeze([
    "Core network 100 series",
    "Wisconsin network 600 series",
]);

/**
 * Centivo network-series subform. Renders single-select dropdowns for 100/600 series,
 * single-select dropdown for 400 series, multi-select drawers for other series, and a multi-select drawer for preferred 900-series.
 *
 * @param {object} props
 * @param {Record<string, Array<string>>} props.seriesOptions - Series name to code options map.
 * @param {Array<string | Array<string>>} props.networkSeries - Selected code/codes per series index.
 * @param {Array<string>} props.preferredNetworkCodes - Selected preferred 900-series codes.
 * @param {(seriesIndex: number, value: string | Array<string>) => void} props.onSeriesChange
 * @param {(codes: Array<string>) => void} props.onPreferredChange
 * @returns {import('react').JSX.Element}
 */
const CentivoNetworkSeries = ({
    seriesOptions,
    networkSeries,
    preferredNetworkCodes,
    onSeriesChange,
    onPreferredChange,
}) => {
    const seriesKeys = Object.keys(seriesOptions ?? {});
    const preferredSeriesIndex = seriesKeys.indexOf(PREFERRED_SERIES_KEY);
    const preferredSeriesRaw = networkSeries?.[preferredSeriesIndex];
    const preferredOptionsList = Array.isArray(preferredSeriesRaw)
        ? preferredSeriesRaw.filter(Boolean)
        : preferredSeriesRaw
          ? [String(preferredSeriesRaw)]
          : [];
    const showPreferred = preferredOptionsList.length > 0;

    const connectedKeys = seriesKeys.filter((key) =>
        CONNECTED_PAIR_KEYS.includes(key),
    );
    const otherKeys = seriesKeys.filter(
        (key) => !CONNECTED_PAIR_KEYS.includes(key),
    );

    return (
        <div className="config-network-series config-network-series--centivo">
            <h3 className="config-network-series__title">
                You must select at least one series. If you select a 100 series
                then the 600 series is conditional.
            </h3>

            {connectedKeys.length > 0 ? (
                <div className="config-network-series__connected-row">
                    {connectedKeys.map((seriesKey) => {
                        const seriesIndex = seriesKeys.indexOf(seriesKey);
                        const rawValue = networkSeries?.[seriesIndex];
                        const singleValue = Array.isArray(rawValue)
                            ? (rawValue[0] ?? "")
                            : (rawValue ?? "");

                        return (
                            <SelectField
                                key={seriesKey}
                                id={`series-${seriesIndex}`}
                                label={seriesKey}
                                value={singleValue}
                                onChange={(value) =>
                                    onSeriesChange(seriesIndex, value)
                                }
                                placeholderOption="Select"
                                options={(seriesOptions[seriesKey] ?? []).map(
                                    (code) => ({
                                        value: code,
                                        label: code,
                                    }),
                                )}
                            />
                        );
                    })}
                </div>
            ) : null}

            <div className="config-network-series__grid">
                {otherKeys.map((seriesKey) => {
                    const seriesIndex = seriesKeys.indexOf(seriesKey);
                    const rawValue = networkSeries?.[seriesIndex];
                    const isSingle = isSingleSelectSeriesKey(seriesKey);

                    if (isSingle) {
                        const singleValue = Array.isArray(rawValue)
                            ? (rawValue[0] ?? "")
                            : (rawValue ?? "");

                        return (
                            <SelectField
                                key={seriesKey}
                                id={`series-${seriesIndex}`}
                                label={seriesKey}
                                value={singleValue}
                                onChange={(newValue) =>
                                    onSeriesChange(seriesIndex, newValue)
                                }
                                placeholderOption="Select"
                                options={(seriesOptions[seriesKey] ?? []).map(
                                    (code) => ({
                                        value: code,
                                        label: code,
                                    }),
                                )}
                            />
                        );
                    }

                    const multiValue = Array.isArray(rawValue)
                        ? rawValue
                        : rawValue
                          ? [String(rawValue)]
                          : [];

                    return (
                        <MultiSelectDrawer
                            key={seriesKey}
                            id={`series-${seriesIndex}`}
                            label={seriesKey}
                            value={multiValue}
                            onChange={(newValue) =>
                                onSeriesChange(seriesIndex, newValue)
                            }
                            placeholderOption="Select"
                            options={(seriesOptions[seriesKey] ?? []).map(
                                (code) => ({
                                    value: code,
                                    label: code,
                                }),
                            )}
                        />
                    );
                })}
            </div>

            {showPreferred ? (
                <MultiSelectDrawer
                    id="preferred-900-series"
                    label="Preferred Network 900 Series"
                    className="config-network-series__preferred"
                    value={
                        Array.isArray(preferredNetworkCodes)
                            ? preferredNetworkCodes
                            : []
                    }
                    onChange={onPreferredChange}
                    placeholderOption="Select"
                    options={preferredOptionsList.map((code) => ({
                        value: code,
                        label: code,
                    }))}
                />
            ) : null}
        </div>
    );
};

export default CentivoNetworkSeries;
